<template>
  <div :class="$style.container">
    <div :class="$style.titleSection">
      <div :class="$style.title">Data Collection Period</div>
      <button
        v-if="activeAuditList.length > 5 && showViewAllBtn"
        :class="[$style.button, 'button']"
        @click="handleViewAll"
      >
        <span>View all</span>
        <span class="icon">
          <i class="fas fa-chevron-down"></i>
        </span>
      </button>
      <button
        v-if="showViewLessBtn"
        :class="[$style.button, 'button']"
        @click="handleViewLess"
      >
        <span>View less</span>
        <span class="icon">
          <i class="fas fa-chevron-up"></i>
        </span>
      </button>
    </div>

    <div :class="$style.contentSection">
      <p-data-table
        :data="dirtyActiveAuditList"
        :hoverable="true"
        :noDataText="`No data collection period found`"
        :class="$style.table"
      >
        <template slot="thead">
          <tr>
            <td :class="$style.auditTitleCell">Title</td>
            <td width="20%">Type</td>
            <td width="20%">Block</td>
            <td width="20%">Duration</td>
            <td width="20%">Data Collection Start Date</td>
          </tr>
        </template>
        <template slot="tbody" slot-scope="{ data }">
          <tr v-for="item in data" :key="item.id">
            <td
              v-if="
                isDataCollector(item) && new Date(item.startDate) > dateToday
              "
              class="display-flex"
            >
              <p :class="$style.disableAuditTitle">{{ item.name }}</p>
              <b-tooltip
                label="Records cannot be submitted for upcoming data collection periods."
                type="is-dark"
                position="is-right"
                size="is-small"
                style="cursor: pointer"
              >
                <b-icon
                  :class="$style.infoIcon"
                  icon="information-outline"
                  size="is-small"
                >
                </b-icon>
              </b-tooltip>
            </td>
            <td v-else :class="$style.auditTitleCell">
              <router-link :to="getAuditUrl(item)">
                {{ item.name }}
              </router-link>
            </td>
            <td>{{ item.auditCategory }}</td>
            <td>
              {{ item.auditCategory === 'Cycle' ? item.auditCycleNo : '-' }}
            </td>
            <td>{{ getDurationText(item.startDate, item.endDate) }}</td>
            <td>{{ item.startedAt ? formatDate(item.startedAt) : 'N/A' }}</td>
          </tr>
        </template>
      </p-data-table>
    </div>
    <div>
      <button
        v-if="archivedAuditList.length > 0"
        @click="showArchivedList ? hideArchivedAudits() : showArchivedAudits()"
        :class="$style.archivedAuditBtn"
      >
        <span>View Archived Data Collection Period</span>
        <span v-if="showArchivedList" class="icon">
          <i class="fas fa-chevron-up"></i>
        </span>
        <span v-else class="icon">
          <i class="fas fa-chevron-down"></i>
        </span>
      </button>
      <div
        v-if="showArchivedList"
        :class="[$style.contentSection, $style.archivedAuditList]"
      >
        <p-data-table
          :data="dirtyArchivedAuditList"
          :hoverable="true"
          :noDataText="`No data collection period found.`"
          :class="$style.table"
        >
          <template slot="thead">
            <tr>
              <td :class="$style.auditTitleArchiveCell">Title</td>
              <td width="20%">Duration</td>
              <td width="20%">Date Archived</td>
            </tr>
          </template>
          <template slot="tbody" slot-scope="{ data }">
            <tr v-for="item in data" :key="item.id">
              <td v-if="showAuditDetails(item)">
                {{ item.name }}
              </td>
              <td v-else :class="$style.auditTitleArchiveCell">
                <router-link :to="getAuditUrl(item)">
                  {{ item.name }}
                </router-link>
              </td>
              <td>{{ getDurationText(item.startDate, item.endDate) }}</td>
              <td>{{ formatDate(item.updatedAt) }}</td>
            </tr>
          </template>
        </p-data-table>
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator';
import dayjs from 'dayjs';
import { DATE_FORMAT } from '@/utils/date.util';
import moment from 'moment';
import { cloneData } from '@/utils/general.util';
import { AuditDetail } from '../../../store/modules/audits/types/audits.types';
import { UserRoleInAudit } from '@/store/modules/projects/types/projects.types';
import { getAuditUrlByRole } from '@/utils/project.util';

@Component({ components: {} })
export default class AuditList extends Vue {
  @Prop()
  public activeAuditList!: AuditDetail[];
  @Prop()
  public archivedAuditList!: AuditDetail[];

  public dirtyActiveAuditList: AuditDetail[] = [];
  public dirtyArchivedAuditList: AuditDetail[] = [];
  public showViewLessBtn: boolean = false;
  public showViewAllBtn: boolean = true;
  public showArchivedList: boolean = false;
  public dateToday: Date = new Date();

  public formatDate(s: string) {
    return dayjs(s).format(DATE_FORMAT);
  }

  public mounted() {
    this.dirtyActiveAuditList = cloneData(this.activeAuditList);
    this.dirtyArchivedAuditList = cloneData(this.archivedAuditList);
    if (this.dirtyActiveAuditList.length > 5) {
      this.dirtyActiveAuditList = this.activeAuditList.slice(0, 5);
    }
  }

  public isDataCollector(item: AuditDetail): boolean {
    return item.userRoleInAudit === UserRoleInAudit.isDataCollector
      ? true
      : false;
  }

  public getAuditUrl(audit: AuditDetail): string {
    if (audit.userRoleInAudit) {
      return getAuditUrlByRole(audit.id, audit.userRoleInAudit);
    } else {
      return '/audits/' + audit.id + '/homepage#dataCollectionSheet';
    }
  }

  public showAuditDetails(item: AuditDetail) {
    return (
      this.isDataCollector(item) &&
      (new Date(item.startDate) > this.dateToday ||
        new Date(item.endDate) < this.dateToday)
    );
  }

  public getDurationText(startDate: string, endDate: string) {
    if (startDate === null && endDate === null) {
      return 'N/A';
    } else {
      if (endDate !== null) {
        return `${dayjs(startDate).format(DATE_FORMAT)} - ${dayjs(
          endDate,
        ).format(DATE_FORMAT)}`;
      } else {
        return `from ${dayjs(startDate).format(DATE_FORMAT)}`;
      }
    }
  }

  public dataCollectionStatus(auditDetail: any) {
    if (
      auditDetail.endedAt &&
      moment(new Date()).isSameOrAfter(auditDetail.endedAt)
    ) {
      return `Completed on ${this.formatDate(auditDetail.endedAt)}`;
    } else if (
      auditDetail.startedAt &&
      moment(new Date()).isSameOrAfter(auditDetail.startedAt)
    ) {
      return 'Ongoing';
    } else {
      return 'Not Started';
    }
  }

  public handleViewAll() {
    this.showViewLessBtn = true;
    this.showViewAllBtn = false;
    this.dirtyActiveAuditList = cloneData(this.activeAuditList);
  }

  public handleViewLess() {
    this.showViewLessBtn = false;
    this.showViewAllBtn = true;
    this.dirtyActiveAuditList = this.activeAuditList.slice(0, 5);
  }

  public showArchivedAudits() {
    this.showArchivedList = true;
  }

  public hideArchivedAudits() {
    this.showArchivedList = false;
  }
}
</script>
<style lang="scss" module>
.container {
  margin-bottom: 5rem;
  .titleSection {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    .title {
      font-weight: bold;
      text-transform: uppercase;
      padding: 8px 0;
    }
    .button {
      border: none;
      background: transparent;
      color: $primary;
    }
  }
  .contentSection {
    border-radius: 6px;
  }
  .archivedAuditBtn {
    background: unset;
    border: unset;
    font-size: 14px;
    span {
      color: #00205b;
    }
  }
  .archivedAuditList {
    margin-top: 16px;
  }
}
.table {
  table {
    table-layout: fixed;
  }
}
.disableAuditTitle {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #99a6be;
  font-weight: bold;
  cursor: default;
}
.infoIcon {
  display: block !important;
  padding: 2px 0 0 5px;
}
.auditTitleCell {
  width: 20%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.auditTitleArchiveCell {
  width: 60%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
